/**
 * Formula Overlay Component
 * Implements PRD FR-40
 */

import React, { useEffect, useRef } from 'react';
import katex from 'katex';
import { useSimulationStore } from '../state/simulationStore';

interface FormulaProps {
  latex: string;
  displayMode?: boolean;
}

function Formula({ latex, displayMode = true }: FormulaProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      try {
        katex.render(latex, containerRef.current, {
          displayMode,
          throwOnError: false,
          trust: true,
        });
      } catch (e) {
        console.error('KaTeX rendering error:', e);
        if (containerRef.current) {
          containerRef.current.textContent = latex;
        }
      }
    }
  }, [latex, displayMode]);

  return <div ref={containerRef} className="overflow-x-auto" />;
}

interface FormulaSectionProps {
  title: string;
  children: React.ReactNode;
}

function FormulaSection({ title, children }: FormulaSectionProps) {
  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      <div className="bg-gray-50 rounded-lg p-4">{children}</div>
    </div>
  );
}

export function FormulaOverlay() {
  const { showFormulas, toggleFormulas, simulation, params } = useSimulationStore();

  if (!showFormulas) {
    return (
      <button
        onClick={toggleFormulas}
        className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-blue-700 transition-colors z-50"
      >
        Show Formulas
      </button>
    );
  }

  const hasFees = params.eta > 0;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={toggleFormulas}
        className="fixed bottom-4 right-4 bg-gray-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-gray-700 transition-colors z-50"
      >
        Hide Formulas
      </button>

      {/* Overlay Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-40 overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6">
            Grossman-Miller Formulas
          </h3>

          {/* Equilibrium Price */}
          <FormulaSection title="Equilibrium Price at t=1">
            {!hasFees ? (
              <Formula latex="S_1 = \mu - \frac{a \sigma^2 i}{n + 1}" />
            ) : (
              <Formula latex="S_1 = \mu - \frac{a \sigma^2 i}{n + 1} - \frac{2n}{n+1}\eta" />
            )}
          </FormulaSection>

          {/* Holdings */}
          <FormulaSection title="Equilibrium Holdings at t=1">
            <div className="space-y-2">
              <Formula latex="q_1^{MM} = \frac{i}{n+1} \quad \text{(per MM)}" />
              <Formula latex="q_1^{LT1} = \frac{i}{n+1}" />
            </div>
          </FormulaSection>

          {/* Liquidity Discount */}
          <FormulaSection title="Liquidity Discount">
            {!hasFees ? (
              <Formula latex="\text{Discount} = \mu - S_1 = \frac{a \sigma^2 i}{n+1}" />
            ) : (
              <Formula latex="\text{Discount} = \underbrace{\frac{a \sigma^2 i}{n+1}}_{\text{Risk}} + \underbrace{\frac{2n}{n+1}\eta}_{\text{Fee}}" />
            )}
          </FormulaSection>

          {/* Immediacy */}
          <FormulaSection title="Immediacy Measure">
            <Formula latex="\text{Immediacy} = \frac{n}{n+1}" />
            <p className="text-xs text-gray-600 mt-2">
              Fraction of LT1's desired trade completed at t=1
            </p>
          </FormulaSection>

          {/* Price at t=2 */}
          <FormulaSection title="Efficient Price at t=2">
            <Formula latex="S_2 = \mu + \epsilon_2" />
            <p className="text-xs text-gray-600 mt-2">
              With balanced order flow, price equals conditional expectation
            </p>
          </FormulaSection>

          {/* Terminal Value */}
          <FormulaSection title="Terminal Value at t=3">
            <Formula latex="S_3 = \mu + \epsilon_2 + \epsilon_3" />
          </FormulaSection>

          {/* Risk Metrics */}
          <FormulaSection title="Risk Metrics">
            <div className="space-y-2">
              <Formula latex="\text{Var}(\Delta W) = q_1^2 \sigma^2" />
              <Formula latex="E[S_2 - S_1] = \frac{a \sigma^2 i}{n+1}" />
            </div>
          </FormulaSection>

          {/* Autocorrelation */}
          <FormulaSection title="Price Autocorrelation">
            <Formula latex="q = -\frac{t}{1+t}" />
            <p className="text-xs text-gray-600 mt-2">
              where t = a² · Var₁(S₂) · Var(i) / (1+n)²
            </p>
          </FormulaSection>

          {/* Current Values */}
          {simulation && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Current Parameter Values
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>n = {params.n}</div>
                <div>i = {params.i}</div>
                <div>μ = {params.mu}</div>
                <div>σ = {params.sigma}</div>
                <div>a = {params.a}</div>
                {hasFees && <div>η = {params.eta}</div>}
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mt-4 mb-3">
                Computed Values
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>S₁ = {simulation.equilibrium.S1.toFixed(4)}</div>
                <div>S₂ = {simulation.equilibrium.S2.toFixed(4)}</div>
                <div>Discount = {simulation.equilibrium.liquidityDiscount.toFixed(4)}</div>
                <div>Immediacy = {(simulation.equilibrium.immediacy * 100).toFixed(1)}%</div>
              </div>
            </div>
          )}

          {/* Reference */}
          <div className="mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500">
            <p>
              Reference: Grossman, S.J. and Miller, M.H. (1988), "Liquidity and Market
              Structure," <em>The Journal of Finance</em>, Vol. 43, No. 3, pp. 617-633.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
